import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiDownload,
  FiFileText,
  FiInfo,
  FiX,
} from "react-icons/fi";
import StatusChip from "../../../../Common/StatusChip";
import { quoteChipType } from "../Quotes/quotesService";
import { formatAmountWithCommas } from "../../../../../Utils/utils";
import { useSession } from "../../../../../context/SessionContext";
import { UpdateComparisonApprovalApi } from "../../../../../Api/Procurement/QuoteComparison";

const QuotesComparisonDetailsModal = ({ open, onClose, comparison, onChanged }) => {
  const { session } = useSession();
  const [comments, setComments] = useState("");
  const [busy, setBusy] = useState(false);

  if (!open || !comparison) {
    return null;
  }

  const vendors = comparison.Vendors || [];
  const items = comparison.Items || [];
  const approvals = comparison.Approvals || [];
  const awardOf = (item) =>
    (comparison.Lines || []).find((l) => (l.PurchaseRequisitionLineId || l.MaterialId) === item.id);

  // Approve / Reject for the logged-in approver whose decision is still pending
  const myApproval =
    comparison.Status === "Pending Approval"
      ? approvals.find((a) => a.ApproverUserId === session?.LoginId && a.Status === "Pending")
      : null;

  const decide = async (status) => {
    setBusy(true);
    try {
      const res = await UpdateComparisonApprovalApi(myApproval.Id, status, comments);
      if (res?.data?.IsSuccess) {
        toast.success(`Comparison ${status.toLowerCase()}`);
        setComments("");
        onChanged?.();
      } else {
        toast.error(res?.data?.Message || "Failed to update approval");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal show={open} onHide={onClose} size="xl" centered>
      <Modal.Header
        style={{
          borderBottom: "1px solid #e9ecef",
          backgroundColor: "#f8fbfb",
          padding: "16px 24px",
        }}
      >
        <div className="d-flex justify-content-between align-items-center w-100">
          <div className="d-flex align-items-center" style={{ gap: "12px" }}>
            <h5 className="m-0" style={{ color: "#056363", fontWeight: 700 }}>
              Comparative Statement: {comparison.CSNumber}
            </h5>
            <StatusChip
              status={comparison.Status || "Pending Approval"}
              type={quoteChipType(comparison.Status || "Pending Approval")}
            />
          </div>
          <button
            type="button"
            className="btn btn-link text-muted p-0"
            onClick={onClose}
            style={{ fontSize: "1.2rem", textDecoration: "none" }}
          >
            <FiX />
          </button>
        </div>
      </Modal.Header>

      <Modal.Body style={{ padding: "24px" }}>
        {comparison.IsBlocked && (
          <div
            className="mb-3 d-flex align-items-center"
            style={{
              gap: "8px",
              color: "#b06b00",
              backgroundColor: "#ffe5b4",
              borderRadius: "6px",
              padding: "8px 12px",
              fontSize: "13px",
            }}
          >
            <FiAlertTriangle />
            <span>{comparison.BlockedReason || "This comparison cannot be approved."}</span>
          </div>
        )}
        {/* Reference Details Box */}
        <div
          style={{
            backgroundColor: "#f9fafb",
            borderRadius: "8px",
            border: "1px solid #e5e7eb",
            padding: "16px 20px",
            marginBottom: "20px",
          }}
        >
          <div className="row g-3">
            <div className="col-md-3">
              <span className="text-muted d-block" style={{ fontSize: "12px" }}>
                CS Number
              </span>
              <strong style={{ color: "#056363", fontSize: "14px" }}>{comparison.CSNumber}</strong>
            </div>

            <div className="col-md-3">
              <span className="text-muted d-block" style={{ fontSize: "12px" }}>
                Project / Site
              </span>
              <strong style={{ color: "#111827", fontSize: "14px" }}>
                {comparison.ProjectName}
              </strong>
            </div>

            <div className="col-md-3">
              <span className="text-muted d-block" style={{ fontSize: "12px" }}>
                Linked Requisition
              </span>
              <span
                className="badge badge-info"
                style={{
                  backgroundColor: "#e0f2fe",
                  color: "#0369a1",
                  fontSize: "12px",
                  padding: "4px 8px",
                  fontWeight: 600,
                }}
              >
                {comparison.RequisitionLabel || comparison.RequisitionNo}
              </span>
            </div>

            <div className="col-md-3">
              <span className="text-muted d-block" style={{ fontSize: "12px" }}>
                CS Date
              </span>
              <span style={{ fontSize: "13px", color: "#374151" }}>{comparison.CSDate}</span>
            </div>
          </div>
        </div>

        {/* Rate Comparison Matrix */}
        <h6 style={{ color: "#056363", fontWeight: 700, marginBottom: "12px" }}>
          Rate Comparison Matrix
        </h6>

        <div className="table-responsive border" style={{ borderRadius: "8px" }}>
          <table className="table table-bordered mb-0" style={{ fontSize: "13px" }}>
            <thead style={{ backgroundColor: "#F3F4F6" }}>
              <tr>
                <th
                  style={{
                    width: "28%",
                    verticalAlign: "middle",
                    color: "#374151",
                    fontWeight: 600,
                    padding: "12px",
                  }}
                >
                  <div className="d-flex align-items-center" style={{ gap: "6px" }}>
                    <span>Material & Requested Qty.</span>
                    <FiInfo size="14" color="#6b7280" />
                  </div>
                </th>
                {vendors.map((v, idx) => (
                  <th
                    key={idx}
                    style={{
                      width: `${72 / vendors.length}%`,
                      verticalAlign: "top",
                      padding: "12px",
                      backgroundColor: v.Awarded ? "#f0fdf4" : "#F3F4F6",
                    }}
                  >
                    <div style={{ fontSize: "11px", color: "#6b7280", textTransform: "uppercase" }}>
                      Vendor {v.slot || idx + 1}
                    </div>
                    <div style={{ fontWeight: 700, color: "#111827", fontSize: "13px" }}>
                      {v.VendorName || "—"}
                    </div>
                    {v.AttachmentName && (
                      <div
                        className="d-inline-flex align-items-center mt-1"
                        style={{
                          backgroundColor: "#fff",
                          border: "1px solid #d1d5db",
                          borderRadius: "4px",
                          padding: "2px 8px",
                          fontSize: "11px",
                          gap: "6px",
                          color: "#374151",
                        }}
                      >
                        <FiFileText color="#dc2626" size="12" />
                        {v.AttachmentUrl ? (
                          <a
                            href={v.AttachmentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "inherit" }}
                          >
                            {v.AttachmentName}
                          </a>
                        ) : (
                          <span>{v.AttachmentName}</span>
                        )}
                        <FiDownload
                          size="11"
                          style={{ cursor: "pointer" }}
                          onClick={() => v.AttachmentUrl && window.open(v.AttachmentUrl, "_blank")}
                        />
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {/* Material Rows */}
              {items.map((item, rowIdx) => {
                // Find lowest rate among vendors that supplied this item
                const validRates = vendors
                  .map((v) => v.Rates?.[item.id])
                  .filter((r) => r && r.supplied && Number(r.unitRate) > 0)
                  .map((r) => Number(r.unitRate));
                const minRate = validRates.length > 0 ? Math.min(...validRates) : null;

                return (
                  <tr key={rowIdx}>
                    <td
                      style={{
                        verticalAlign: "middle",
                        fontWeight: 600,
                        color: "#1f2937",
                        padding: "12px",
                        backgroundColor: "#fafafa",
                      }}
                    >
                      {item.label || `${item.materialName} (${item.qty} ${item.uom})`}
                      {awardOf(item) && (
                        <div className="text-muted" style={{ fontSize: "11px", fontWeight: 400 }}>
                          Awarded: {awardOf(item).WinnerVendorName} - {awardOf(item).Quantity}{" "}
                          {item.uom}
                          {awardOf(item).Reason ? ` - Reason: ${awardOf(item).Reason}` : ""}
                          {awardOf(item).PoNumber ? ` - PO ${awardOf(item).PoNumber}` : ""}
                        </div>
                      )}
                    </td>

                    {vendors.map((v, vIdx) => {
                      const rateInfo = v.Rates?.[item.id];
                      const isSupplied = rateInfo?.supplied;
                      const isLowest =
                        isSupplied && minRate !== null && Number(rateInfo.unitRate) === minRate;
                      // the winning vendor's cell is priced at the awarded quantity
                      const award = awardOf(item);
                      const wonQty =
                        isSupplied && award && award.WinnerQuoteId === v.QuoteId
                          ? Number(award.Quantity)
                          : null;

                      return (
                        <td
                          key={vIdx}
                          style={{
                            verticalAlign: "middle",
                            padding: "12px",
                            backgroundColor: isLowest ? "#dcfce7" : "transparent",
                          }}
                        >
                          {isSupplied ? (
                            <div className="d-flex justify-content-between align-items-center">
                              <span
                                style={{
                                  backgroundColor: "#fff",
                                  border: "1px solid #d1d5db",
                                  borderRadius: "4px",
                                  padding: "3px 8px",
                                  fontSize: "12px",
                                  fontWeight: 600,
                                }}
                              >
                                {formatAmountWithCommas(rateInfo.unitRate)} / {item.uom}
                              </span>
                              <span style={{ fontWeight: 700, color: "#111827", fontSize: "13px" }}>
                                {formatAmountWithCommas(
                                  wonQty != null
                                    ? rateInfo.unitRate * wonQty
                                    : rateInfo.total || rateInfo.unitRate * item.qty,
                                )}
                                {wonQty != null && (
                                  <div
                                    className="text-muted"
                                    style={{
                                      fontSize: "11px",
                                      fontWeight: 400,
                                      textAlign: "right",
                                    }}
                                  >
                                    Awarded: {wonQty} {item.uom}
                                  </div>
                                )}
                              </span>
                            </div>
                          ) : (
                            <div
                              className="text-muted"
                              style={{ fontStyle: "italic", fontSize: "12px" }}
                            >
                              {rateInfo?.note || "-- (Not Supplied / N/A)"}
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}

              {/* Carriage / Freight Row */}
              <tr>
                <td
                  style={{
                    verticalAlign: "middle",
                    fontWeight: 600,
                    padding: "12px",
                    backgroundColor: "#fafafa",
                  }}
                >
                  Carriage / Freight (PKR)
                </td>
                {vendors.map((v, idx) => (
                  <td key={idx} style={{ verticalAlign: "middle", padding: "12px" }}>
                    <span style={{ fontWeight: 500 }}>
                      {formatAmountWithCommas(v.CarriageFreight || 0)}
                      {v.CarriageNote ? ` (${v.CarriageNote})` : ""}
                    </span>
                  </td>
                ))}
              </tr>

              {/* Payment & Credit Terms Row */}
              <tr>
                <td
                  style={{
                    verticalAlign: "middle",
                    fontWeight: 600,
                    padding: "12px",
                    backgroundColor: "#fafafa",
                  }}
                >
                  Payment & Credit Terms
                </td>
                {vendors.map((v, idx) => (
                  <td key={idx} style={{ verticalAlign: "middle", padding: "12px" }}>
                    <span style={{ fontWeight: 500, color: "#056363" }}>
                      {v.PaymentTerms || "Standard"}
                    </span>
                  </td>
                ))}
              </tr>

              {/* Award Bidder Row */}
              <tr style={{ backgroundColor: "#f9fafb" }}>
                <td style={{ verticalAlign: "middle", fontWeight: 600, padding: "12px" }}>
                  Award Bidder (Action Checks)
                </td>
                {vendors.map((v, idx) => (
                  <td
                    key={idx}
                    style={{
                      verticalAlign: "middle",
                      padding: "12px",
                      backgroundColor: v.Awarded ? "#dcfce7" : "transparent",
                    }}
                  >
                    <div className="d-flex align-items-center" style={{ gap: "6px" }}>
                      {v.Awarded ? (
                        <>
                          <FiCheckCircle size="16" color="#166534" />
                          <strong style={{ color: "#166534", fontSize: "13px" }}>Awarded</strong>
                        </>
                      ) : (
                        <span className="text-muted" style={{ fontSize: "12px" }}>
                          Not Selected
                        </span>
                      )}
                    </div>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>

        {/* Remarks / Award Summary */}
        {comparison.Remarks && (
          <div
            className="mt-3"
            style={{
              backgroundColor: "#eff6ff",
              border: "1px solid #bfdbfe",
              borderRadius: "6px",
              padding: "12px 16px",
              fontSize: "13px",
            }}
          >
            <strong style={{ color: "#1e40af" }}>
              Evaluation Notes & Approval Recommendation:
            </strong>
            <p className="m-0 mt-1" style={{ color: "#1e3a8a" }}>
              {comparison.Remarks}
            </p>
          </div>
        )}

        {/* Approvals */}
        {approvals.length > 0 && (
          <div className="mt-3">
            <strong style={{ color: "#056363", fontSize: "13px" }}>Approvals</strong>
            <table className="table table-bordered table-sm mt-1" style={{ fontSize: "13px" }}>
              <thead style={{ backgroundColor: "#F3F4F6" }}>
                <tr>
                  <th>Approver</th>
                  <th>Status</th>
                  <th>Comments</th>
                </tr>
              </thead>
              <tbody>
                {approvals.map((a) => (
                  <tr key={a.Id}>
                    <td>{a.ApproverName || a.ApproverUserId}</td>
                    <td>{a.Status}</td>
                    <td>{a.Comments || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {myApproval && (
          <div className="mt-2">
            <label className="input_label" style={{ fontSize: "12px" }}>
              Comments:
            </label>
            <textarea
              className="input_styling w-100"
              rows={2}
              value={comments}
              onChange={(e) => setComments(e.target.value)}
            />
            <div className="text-right mt-2">
              <button
                type="button"
                className="btn btn-outline-danger px-4 mr-2"
                disabled={busy}
                onClick={() => decide("Rejected")}
              >
                Reject
              </button>
              <button
                type="button"
                className="btn btn-success px-4"
                disabled={busy}
                onClick={() => decide("Approved")}
              >
                Approve
              </button>
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer style={{ borderTop: "1px solid #e9ecef", padding: "12px 24px" }}>
        <button type="button" className="btn btn-secondary px-4" onClick={onClose}>
          Close
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default QuotesComparisonDetailsModal;
